import { useState } from 'react';
import {
    Alert, Anchor, Box, Breadcrumbs, Button, Center, Group, Loader, SimpleGrid, Stack, Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AlertTriangle, Plus } from 'lucide-react';
import type { ProviderConnection } from '@bmai/connections-api-client';
import type { ProviderType } from '@bmai/shared';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import IntegrationConnectionCard from './IntegrationConnectionCard';
import IntegrationProviderTypeGrid from './IntegrationProviderTypeGrid';
import { type IntegrationsPageViewModel, type IntegrationSaveResult } from './IntegrationsPageViewModel';
import IntegrationConnectionWizard from './wizard/IntegrationConnectionWizard';
import type { LaunchUrlResolver } from './wizard/ConnectionStepAuth';

interface IntegrationsPageLayoutProps {
    vm: IntegrationsPageViewModel;
    title: string;
    subtitle: string;
    addLabel: string;
    emptyTitle: string;
    emptyDescription: string;
    /** Optional client-side resolver for launch-action URLs. See LaunchUrlResolver. */
    onResolveLaunchUrl?: LaunchUrlResolver;
}

interface OauthCancelDialog {
    providerDisplayName: string;
    message: string;
}

export default function IntegrationsPageLayout({
    vm, title, subtitle, addLabel, emptyTitle, emptyDescription, onResolveLaunchUrl,
}: IntegrationsPageLayoutProps): React.ReactElement {
    const [oauthCancelDialog, setOauthCancelDialog] = useState<OauthCancelDialog | null>(null);

    async function handleOauth(): Promise<IntegrationSaveResult> {
        const result = await vm.startOauth();
        if (result.ok) {
            notifications.show({
                title: 'Signed in',
                message: `Continue setting up ${result.providerDisplayName}.`,
                color: 'green',
            });
            setOauthCancelDialog(null);
        } else if (result.errorSubcode === 'cancel') {
            setOauthCancelDialog({
                providerDisplayName: result.providerDisplayName,
                message: result.error ?? 'Authorization was cancelled.',
            });
        } else if (result.error) {
            notifications.show({ title: 'Authorization failed', message: result.error, color: 'red' });
        }
        return result;
    }

    async function handleRequestAdminConsent(): Promise<void> {
        setOauthCancelDialog(null);
        const result = await vm.requestAdminConsent();
        if (result.ok) {
            notifications.show({
                title: 'Admin consent granted',
                message: `Tenant-wide consent recorded for ${result.providerDisplayName}. Click Connect to finish setting up the connection.`,
                color: 'green',
                autoClose: 8000,
            });
        } else if (result.errorSubcode === 'cancel') {
            setOauthCancelDialog({
                providerDisplayName: result.providerDisplayName,
                message: result.error ?? 'Admin consent didn\'t complete.',
            });
        } else if (result.error) {
            notifications.show({ title: 'Admin consent failed', message: result.error, color: 'red' });
        }
    }

    async function handleCheckAgain(): Promise<void> {
        setOauthCancelDialog(null);
        await handleOauth();
    }

    async function handleInstallStart(): Promise<IntegrationSaveResult> {
        const result = await vm.startInstallFlow();
        if (result.ok) {
            notifications.show({
                title: 'Install complete',
                message: `Review and save to finish setting up ${result.providerDisplayName}.`,
                color: 'green',
            });
            return result;
        }
        if (result.suggestFallback) {
            notifications.show({
                title: "Couldn't auto-detect the install",
                message: "If you finished installing, enter the requested details in the fallback field to finish connecting.",
                color: 'blue',
            });
            return result;
        }
        if (result.error) {
            notifications.show({ title: 'Install failed', message: result.error, color: 'red' });
        }
        return result;
    }

    async function handleInstallFallback(value: string): Promise<IntegrationSaveResult> {
        const result = await vm.createConnectionFromInstallFallback(value);
        if (result.ok) {
            notifications.show({
                title: 'Install complete',
                message: `Review and save to finish setting up ${result.providerDisplayName}.`,
                color: 'green',
            });
        } else if (result.error) {
            notifications.show({ title: 'Connection failed', message: result.error, color: 'red' });
        }
        return result;
    }

    async function handleReauthenticate(conn: ProviderConnection): Promise<void> {
        const result = await vm.reauthenticate(conn.id);
        if (result.ok) {
            notifications.show({
                title: 'Re-authenticated',
                message: result.providerDisplayName,
                color: 'green',
            });
        } else if (result.errorSubcode === 'cancel') {
            notifications.show({
                title: 'Re-authentication cancelled',
                message: result.providerDisplayName,
                color: 'gray',
            });
        } else if (result.error) {
            notifications.show({ title: 'Re-authentication failed', message: result.error, color: 'red' });
        }
    }

    async function handleDelete(conn: ProviderConnection): Promise<void> {
        const displayName = vm.providerTypes.find((p) => p.id === conn.providerTypeId)?.displayName ?? conn.providerTypeId;
        if (!confirm(`Delete connection "${displayName}"?`)) return;
        const result = await vm.deleteConnection(conn.id);
        if (result.ok) {
            notifications.show({ title: 'Connection deleted', message: displayName, color: 'green' });
        } else if (result.error) {
            notifications.show({ title: 'Error', message: result.error, color: 'red' });
        }
    }

    if (vm.loading && vm.connections.length === 0 && vm.providerTypes.length === 0) {
        return <Center py="xl"><Loader /></Center>;
    }

    const wizardOpen = vm.modalOpen;
    const providerType = vm.selectedProviderType;

    return (
        <Box>
            <PageHeaderComponent
                title={title}
                subtitle={subtitle}
                rightToolbar={
                    !wizardOpen ? (
                        <Button leftSection={<Plus size={16} />} onClick={() => vm.openCreate()}>
                            {addLabel}
                        </Button>
                    ) : undefined
                }
            />

            {vm.loadError && !wizardOpen && (
                <Alert
                    variant="light"
                    color="red"
                    icon={<AlertTriangle size={16} />}
                    title="Failed to load"
                    mb="md"
                >
                    <Text size="sm">{vm.loadError}</Text>
                </Alert>
            )}

            {wizardOpen ? (
                <WizardSurface
                    vm={vm}
                    providerType={providerType}
                    title={title}
                    onOauth={handleOauth}
                    onInstallStart={handleInstallStart}
                    onInstallFallback={handleInstallFallback}
                    onResolveLaunchUrl={onResolveLaunchUrl}
                    onReauthenticate={() => {
                        const conn = vm.editingId ? vm.connections.find((c) => c.id === vm.editingId) : undefined;
                        if (conn) void handleReauthenticate(conn);
                    }}
                />
            ) : (
                <>
                    {vm.connections.length === 0 ? (
                        <Box
                            p="xl"
                            style={{
                                border: '1px dashed var(--bm-color-border)',
                                borderRadius: 'var(--bm-radius)',
                                textAlign: 'center',
                            }}
                        >
                            <Stack align="center" gap={4}>
                                <Text fw={600}>{emptyTitle}</Text>
                                <Text size="sm" c="dimmed">{emptyDescription}</Text>
                            </Stack>
                        </Box>
                    ) : (
                        <SimpleGrid cols={{ base: 1, sm: 2, lg: 2, xl: 3 }} spacing="md">
                            {vm.connections.map((c) => (
                                <IntegrationConnectionCard
                                    key={c.id}
                                    connection={c}
                                    providerType={vm.providerTypes.find((p) => p.id === c.providerTypeId)}
                                    status={vm.statusFor(c.id)}
                                    onEdit={(conn) => vm.openEdit(conn)}
                                    onDelete={handleDelete}
                                    onReauthenticate={handleReauthenticate}
                                />
                            ))}
                        </SimpleGrid>
                    )}
                </>
            )}

            {oauthCancelDialog && (
                <OauthCancelInline
                    dialog={oauthCancelDialog}
                    saving={vm.saving}
                    onDismiss={() => setOauthCancelDialog(null)}
                    onCheckAgain={handleCheckAgain}
                    onRequestAdminConsent={handleRequestAdminConsent}
                />
            )}
        </Box>
    );
}

interface WizardSurfaceProps {
    vm: IntegrationsPageViewModel;
    providerType: ProviderType | null;
    title: string;
    onOauth: () => Promise<IntegrationSaveResult>;
    onInstallStart: () => Promise<IntegrationSaveResult>;
    onInstallFallback: (value: string) => Promise<IntegrationSaveResult>;
    onReauthenticate: () => void;
    onResolveLaunchUrl?: LaunchUrlResolver;
}

function WizardSurface({
    vm,
    providerType,
    title,
    onOauth,
    onInstallStart,
    onInstallFallback,
    onReauthenticate,
    onResolveLaunchUrl,
}: WizardSurfaceProps): React.ReactElement {
    if (!vm.editing && !providerType) {
        return (
            <Stack gap="sm" mt="md">
                <Group justify="space-between" align="center">
                    <WizardBreadcrumb rootLabel={title} stepLabel="Choose a provider" onRoot={() => vm.closeModal()} />
                    <Button variant="subtle" size="xs" onClick={() => vm.closeModal()}>
                        Cancel
                    </Button>
                </Group>
                <IntegrationProviderTypeGrid
                    providerTypes={vm.providerTypes}
                    selectedId={vm.form.providerTypeId}
                    onSelect={(id) => vm.selectProviderType(id)}
                />
            </Stack>
        );
    }

    if (!providerType || !vm.form.authMethod) {
        return <Center py="xl"><Loader /></Center>;
    }

    return (
        <Box mt="md">
            <WizardBreadcrumb
                rootLabel={title}
                stepLabel={vm.editing ? 'Edit connection' : 'Choose a provider'}
                providerName={providerType.displayName}
                onRoot={() => vm.closeModal()}
                onStep={vm.editing ? undefined : (): void => vm.openCreate()}
            />
            <IntegrationConnectionWizard
                vm={vm}
                providerType={providerType}
                onClose={() => vm.closeModal()}
                onSaveResult={(result) => {
                    if (result.ok) {
                        notifications.show({
                            title: result.editing ? 'Connection updated' : 'Connection created',
                            message: result.providerDisplayName,
                            color: 'green',
                        });
                    } else if (result.error) {
                        notifications.show({ title: 'Error', message: result.error, color: 'red' });
                    }
                }}
                onStartOauth={onOauth}
                onStartInstall={onInstallStart}
                onSubmitInstallFallback={onInstallFallback}
                onReauthenticate={onReauthenticate}
                onResolveLaunchUrl={onResolveLaunchUrl}
            />
        </Box>
    );
}

interface WizardBreadcrumbProps {
    /** Root crumb — the page name; always navigates back to the connection list. */
    rootLabel: string;
    /** Middle crumb — "Add AI provider" / "Edit connection". Clickable only when `onStep` is set. */
    stepLabel: string;
    /** Trailing crumb — the chosen provider's name; omitted on the provider-selection step. */
    providerName?: string;
    onRoot: () => void;
    /** Back to provider selection; omit to render `stepLabel` as the current (non-clickable) crumb. */
    onStep?: () => void;
}

function WizardBreadcrumb({ rootLabel, stepLabel, providerName, onRoot, onStep }: WizardBreadcrumbProps): React.ReactElement {
    return (
        <Breadcrumbs separator="›" mb="md">
            <Anchor size="sm" c="dimmed" onClick={onRoot}>{rootLabel}</Anchor>
            {onStep ? (
                <Anchor size="sm" c="dimmed" onClick={onStep}>{stepLabel}</Anchor>
            ) : (
                <Text size="sm" c={providerName ? 'dimmed' : undefined} fw={providerName ? undefined : 500}>{stepLabel}</Text>
            )}
            {providerName && <Text size="sm" fw={500}>{providerName}</Text>}
        </Breadcrumbs>
    );
}

interface OauthCancelInlineProps {
    dialog: OauthCancelDialog;
    saving: boolean;
    onDismiss: () => void;
    onCheckAgain: () => void;
    onRequestAdminConsent: () => void;
}

function OauthCancelInline({ dialog, saving, onDismiss, onCheckAgain, onRequestAdminConsent }: OauthCancelInlineProps): React.ReactElement {
    return (
        <Alert
            variant="light"
            color="yellow"
            icon={<AlertTriangle size={16} />}
            title="Authorization didn't complete"
            mt="md"
            withCloseButton
            onClose={onDismiss}
        >
            <Stack gap="sm">
                <Text size="sm">{dialog.message}</Text>
                <Text size="sm" c="dimmed">
                    If admin approval is required, request it now. If your admin already
                    approved (e.g. directly in the Entra portal), use Check again.
                </Text>
                <Group justify="flex-end" mt="sm">
                    <Button variant="default" onClick={onCheckAgain} loading={saving}>
                        Check again
                    </Button>
                    <Button onClick={onRequestAdminConsent} loading={saving}>
                        Request admin approval
                    </Button>
                </Group>
            </Stack>
        </Alert>
    );
}
